/* ----------------------------------------------------------------------------
 * Category selection — PURE, dependency-free model behind the "Push to WordPress
 * Draft" category picker. DOM-free so it is unit-testable under this repo's
 * `node --test --experimental-strip-types` convention (WordPressPushControl is a
 * thin view over these helpers).
 *
 * INVARIANT (mirrors the server CategorySelector): a draft is pushed with the most
 * relevant EXISTING category — never an auto-created term, and NEVER silently
 * defaulted to "Uncategorized". When the server is confident (decision 'selected')
 * the suggested existing category is used and shown (the user can still change it);
 * when it is ambiguous / low-confidence / no-match (decision 'needs_choice') the
 * push is BLOCKED until the human explicitly chooses an existing category. The
 * selected id + the site's live category ids travel in push_options so the server
 * can admit the genuinely-existing term without creating anything.
 * -------------------------------------------------------------------------- */

/** One existing category on the connected site (server wp/v2 projection). */
export interface WpCategory {
  id: number;
  slug: string;
  name: string;
}

export type CategoryDecision = 'selected' | 'needs_choice';
export type CategoryConfidence = 'high' | 'low' | 'none';

/** The server's best-match verdict (ConnectorCategoriesRoutes -> CategorySelector). */
export interface CategorySuggestion {
  decision: CategoryDecision;
  confidence: CategoryConfidence;
  term_id: number | null;
  slug: string | null;
  name: string | null;
  candidates: { id: number; slug: string; name: string; score: number }[];
}

/** GET /v1/connector/connections/{id}/categories response. */
export interface CategoryReadResponse {
  ok?: boolean;
  target_id?: number;
  site_url?: string;
  topic?: string;
  categories?: WpCategory[];
  suggestion?: CategorySuggestion | null;
}

/** The keyword the server matches against: the Yoast focus keyphrase (primary SEO
 *  keyword) if present, else the draft title. Never fabricated. */
export function pushCategoryKeyword(draft: { focus_keyphrase?: string; title?: string }): string {
  const kw = (draft.focus_keyphrase ?? '').trim();
  if (kw !== '') return kw;
  return (draft.title ?? '').trim();
}

export type CategoryGateStatus = 'loading' | 'auto' | 'needs_choice' | 'no_categories';

export interface CategoryGate {
  status: CategoryGateStatus;
  /** the id that WILL be sent (auto suggestion, or the user's explicit pick). */
  selectedId: number | null;
  /** true => the user MUST pick an existing category before the push is allowed. */
  needsChoice: boolean;
  /** whether the push may proceed as far as the category is concerned. */
  canProceed: boolean;
  /** an id to PRE-HIGHLIGHT in the picker (a confident pick, or a low-confidence hint). */
  suggestedId: number | null;
}

/**
 * Resolve the category gate for the current site + draft. Rules:
 *  - loading           -> block until the categories/suggestion arrive.
 *  - no categories     -> the site has none to choose from; push proceeds WITHOUT
 *                         a category (honest degrade; nothing is invented).
 *  - decision selected -> the confident existing suggestion is used (auto); the
 *                         user may override with any existing category.
 *  - needs_choice      -> BLOCK the push until the user explicitly picks an existing
 *                         category (a low-confidence term_id is only pre-highlighted).
 * A user pick is honoured ONLY when it is a real existing category id.
 */
export function categoryGate(input: {
  isLoading: boolean;
  categories: WpCategory[];
  suggestion: CategorySuggestion | null | undefined;
  userChoiceId: number | null;
}): CategoryGate {
  if (input.isLoading) {
    return { status: 'loading', selectedId: null, needsChoice: false, canProceed: false, suggestedId: null };
  }
  const categories = input.categories ?? [];
  if (categories.length === 0) {
    return { status: 'no_categories', selectedId: null, needsChoice: false, canProceed: true, suggestedId: null };
  }

  const validIds = new Set(categories.map((c) => c.id));
  const userPick =
    input.userChoiceId != null && validIds.has(input.userChoiceId) ? input.userChoiceId : null;
  const s = input.suggestion ?? null;

  if (s && s.decision === 'selected' && s.term_id != null && validIds.has(s.term_id)) {
    return {
      status: 'auto',
      selectedId: userPick ?? s.term_id,
      needsChoice: false,
      canProceed: true,
      suggestedId: s.term_id,
    };
  }

  // Ambiguous / low-confidence / no-match: the human must choose an existing category.
  const hint = s && s.term_id != null && validIds.has(s.term_id) ? s.term_id : null;
  return {
    status: 'needs_choice',
    selectedId: userPick,
    needsChoice: true,
    canProceed: userPick != null,
    suggestedId: hint,
  };
}

/**
 * The push_options for the selected EXISTING category. Carries the chosen id AND
 * the site's live category ids (category_site_term_ids) so the server admits a
 * genuinely-existing term without a target whitelist — and never creates one. When
 * there is no selection (e.g. a site with no categories) NO category option is sent
 * (the draft is pushed without a category; the server never defaults to Uncategorized).
 */
export function buildCategoryPushOptions(
  selectedId: number | null,
  categories: WpCategory[],
): Record<string, unknown> {
  const cats = categories ?? [];
  if (selectedId == null || cats.length === 0) {
    return {};
  }
  return {
    category_term_ids: [selectedId],
    category_site_term_ids: cats.map((c) => c.id),
  };
}

/** A short, honest reason line for the category state (drives the picker copy). */
export function categoryGateNote(gate: CategoryGate, suggestion: CategorySuggestion | null | undefined): string {
  switch (gate.status) {
    case 'loading':
      return 'Checking the site’s categories…';
    case 'no_categories':
      return 'This site has no categories yet — the draft will be pushed without one.';
    case 'auto': {
      const name = suggestion?.name ? `“${suggestion.name}”` : 'a category';
      return `Suggested category: ${name} (matched to the topic). You can change it before pushing.`;
    }
    case 'needs_choice':
    default:
      if (suggestion?.confidence === 'none') {
        return 'No category clearly matched this topic — choose an existing one before pushing.';
      }
      return 'More than one category could fit — choose the right existing one before pushing.';
  }
}

/** The category name for an id (for the display chip); '' when unknown. */
export function categoryNameFor(id: number | null, categories: WpCategory[]): string {
  if (id == null) return '';
  const c = (categories ?? []).find((x) => x.id === id);
  return c ? c.name : '';
}
